import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faHome } from "@fortawesome/free-solid-svg-icons";
import { Breadcrumb, Button, Card, Col, Form, Row, Table } from "@themesberg/react-bootstrap";
import { Link, useParams, generatePath } from "react-router-dom";

import { Routes } from "../routes";
import defaultGroups from "../data/groups";
import { enqueueSync } from "../utils/syncQueue";
import { apiRequest, getAuthToken } from "../utils/api";

const GROUPS_STORAGE_KEY = "treasurer.groups";
const EXPENSES_STORAGE_KEY = "treasurer.expenses";

const loadGroups = () => {
  const stored = localStorage.getItem(GROUPS_STORAGE_KEY);
  if (!stored) {
    return defaultGroups;
  }
  try {
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      return defaultGroups;
    }
    return parsed.map((group) => ({
      ...group,
      frequency: group.frequency || "monthly",
      members: Array.isArray(group.members)
        ? group.members
            .map((member) => {
              if (typeof member === "string") {
                return { id: null, name: member, phone: "", info: "" };
              }
              return {
                id: member?.id ?? null,
                name: member?.name || "",
                phone: member?.phone || "",
                info: member?.info || ""
              };
            })
            .filter((member) => member.name)
        : []
    }));
  } catch (error) {
    return defaultGroups;
  }
};

const loadExpenses = () => {
  const stored = localStorage.getItem(EXPENSES_STORAGE_KEY);
  if (!stored) {
    return [];
  }
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const formatAmount = (amount) => {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return "Ksh 0.00";
  }
  return `Ksh ${Number(amount).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const formatTableDate = (timestamp = "") => {
  if (!timestamp) {
    return "--";
  }
  const raw = String(timestamp).trim();
  const datePart = raw.split(/\s+/)[0] || "";
  if (!datePart) {
    return "--";
  }
  const pieces = datePart.split(/[\/.-]/);
  if (pieces.length < 3) {
    return datePart;
  }
  if (pieces[0].length === 4) {
    return `${pieces[2].padStart(2, "0")}/${pieces[1].padStart(2, "0")}/${pieces[0]}`;
  }
  return datePart;
};

export default () => {
  const { groupId } = useParams();
  const [groups, setGroups] = useState(loadGroups);
  const [expenses, setExpenses] = useState(loadExpenses);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenseForm, setExpenseForm] = useState({
    categoryId: "",
    categorySearch: "",
    amount: "",
    expenseDate: "",
    note: ""
  });
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  useEffect(() => {
    localStorage.setItem(EXPENSES_STORAGE_KEY, JSON.stringify(expenses));
  }, [expenses]);

  useEffect(() => {
    localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(groups));
  }, [groups]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      return;
    }
    let isMounted = true;
    const loadRemote = async () => {
      try {
        const [groupData, categoriesData, expensesData] = await Promise.all([
          apiRequest(`/groups/${groupId}`),
          apiRequest("/expense-categories").catch(() => ({ categories: [] })),
          apiRequest(`/groups/${groupId}/expenses`).catch(() => ({ expenses: [] }))
        ]);
        if (!isMounted) {
          return;
        }
        if (groupData?.group) {
          setGroups((prev) => {
            const next = prev.map((item) =>
              String(item.id) === String(groupId)
                ? {
                    ...item,
                    name: groupData.group.name,
                    purpose: groupData.group.purpose,
                    frequency: groupData.group.frequency || "monthly",
                    createdAt: groupData.group.createdAt || item.createdAt
                  }
                : item
            );
            if (next.some((item) => String(item.id) === String(groupId))) {
              return next;
            }
            return [
              ...next,
              {
                id: String(groupData.group.id),
                name: groupData.group.name,
                purpose: groupData.group.purpose,
                frequency: groupData.group.frequency || "monthly",
                createdAt: groupData.group.createdAt || new Date().toISOString(),
                members: []
              }
            ];
          });
        }
        if (categoriesData?.categories) {
          setExpenseCategories(
            categoriesData.categories.map((c) => ({
              id: String(c.id),
              name: c.name || "",
              description: c.description || "",
              createdAt: c.createdAt
            }))
          );
        }
        if (expensesData?.expenses) {
          const normalized = expensesData.expenses.map((e) => ({
            id: String(e.id),
            groupId: String(groupId),
            categoryId: e.categoryId,
            category: e.category,
            categoryName: e.category?.name,
            amount: Number(e.amount || 0),
            expenseDate: e.expenseDate || "",
            note: e.note || "",
            createdAt: e.createdAt
          }));
          setExpenses((prev) => {
            const other = prev.filter((item) => String(item.groupId) !== String(groupId));
            return [...normalized, ...other];
          });
        }
      } catch (error) {
        // keep local data
      }
    };
    loadRemote();
    return () => {
      isMounted = false;
    };
  }, [groupId]);

  const group = useMemo(
    () => groups.find((item) => String(item.id) === String(groupId)),
    [groups, groupId]
  );

  const groupExpenses = useMemo(() => {
    return expenses
      .filter((e) => String(e.groupId) === String(groupId))
      .sort((a, b) => {
        const dA = a.expenseDate || "";
        const dB = b.expenseDate || "";
        if (dA !== dB) {
          return dB.localeCompare(dA);
        }
        return (b.createdAt || "").localeCompare(a.createdAt || "");
      });
  }, [expenses, groupId]);

  const totalExpensesAmount = useMemo(
    () => groupExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0),
    [groupExpenses]
  );

  const categoryDropdownOptions = useMemo(() => {
    const term = expenseForm.categorySearch.trim().toLowerCase();
    if (!term) {
      return expenseCategories;
    }
    return expenseCategories.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(term) ||
        (c.description || "").toLowerCase().includes(term)
    );
  }, [expenseCategories, expenseForm.categorySearch]);

  const selectedCategory = useMemo(() => {
    if (!expenseForm.categoryId) {
      return null;
    }
    return expenseCategories.find((c) => String(c.id) === String(expenseForm.categoryId)) || null;
  }, [expenseCategories, expenseForm.categoryId]);

  const handleExpenseFormChange = (field, value) => {
    setExpenseForm((prev) => ({ ...prev, [field]: value }));
    if (field === "categorySearch") {
      setShowCategoryDropdown(true);
    }
  };

  const handleSelectCategory = (cat) => {
    setExpenseForm((prev) => ({
      ...prev,
      categoryId: cat.id,
      categorySearch: cat.name || ""
    }));
    setShowCategoryDropdown(false);
  };

  const canSaveExpense =
    selectedCategory &&
    expenseForm.amount !== "" &&
    Number(expenseForm.amount) > 0 &&
    expenseForm.expenseDate.trim().length > 0;

  const handleAddExpense = (e) => {
    e.preventDefault();
    if (!canSaveExpense || !group) {
      return;
    }
    const tempId = `exp-${Date.now()}`;
    const categoryId = selectedCategory.id;
    const amount = Number(expenseForm.amount);
    const expenseDate = expenseForm.expenseDate.trim();
    const note = expenseForm.note.trim() || undefined;
    const newExpense = {
      id: tempId,
      groupId: String(groupId),
      categoryId: typeof categoryId === "number" ? categoryId : Number(categoryId),
      category: selectedCategory,
      categoryName: selectedCategory.name,
      amount,
      expenseDate,
      note: note || "",
      createdAt: new Date().toISOString()
    };
    setExpenses((prev) => {
      const other = prev.filter((ex) => String(ex.groupId) !== String(groupId));
      return [...other, newExpense];
    });
    const isServerCategoryId = typeof categoryId === "number" || (typeof categoryId === "string" && !String(categoryId).startsWith("cat-"));
    enqueueSync({
      type: "expense.create",
      payload: {
        groupId: String(groupId),
        groupName: group?.name || "",
        ...(isServerCategoryId ? { categoryId: Number(categoryId) } : { categoryLocalId: String(categoryId), categoryName: selectedCategory.name }),
        amount,
        expenseDate,
        note
      }
    });
    setExpenseForm({ categoryId: "", categorySearch: "", amount: "", expenseDate: "", note: "" });
    setShowCategoryDropdown(false);
    const token = getAuthToken();
    const serverCategoryId = typeof categoryId === "number" ? categoryId : Number(categoryId);
    if (token && navigator.onLine && !Number.isNaN(serverCategoryId)) {
      apiRequest(`/groups/${groupId}/expenses`, {
        method: "POST",
        body: JSON.stringify({ categoryId: serverCategoryId, amount, expenseDate, note })
      })
        .then((data) => {
          if (data?.expense?.id) {
            setExpenses((prev) =>
              prev.map((ex) =>
                ex.id === tempId
                  ? {
                      ...ex,
                      id: String(data.expense.id),
                      category: data.expense.category,
                      categoryName: data.expense.category?.name
                    }
                  : ex
              )
            );
          }
        })
        .catch(() => {});
    }
  };

  const ledgerPath = generatePath(Routes.GroupTransactions.path, { groupId });

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
            <Breadcrumb.Item>
              <FontAwesomeIcon icon={faHome} />
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={Routes.Groups.path}>Groups</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={ledgerPath}>{group?.name || "Group"}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Record expense</Breadcrumb.Item>
          </Breadcrumb>
          <h4>Record expense</h4>
          <p className="mb-0">Record an expense for this group.</p>
        </div>
        <Button
          as={Link}
          to={ledgerPath}
          variant="outline-primary"
          size="sm"
          className="d-inline-flex align-items-center"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to group
        </Button>
      </div>

      <Row>
        <Col xs={12} lg={8}>
          <Card border="light" className="shadow-sm mb-4">
            <Card.Header>
              <h5 className="mb-0">Record expense</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleAddExpense}>
                <Form.Group className="mb-3">
                  <Form.Label>What was it for?</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      placeholder="Choose expense type (e.g. Tent, Food)"
                      value={expenseForm.categorySearch}
                      onChange={(e) => handleExpenseFormChange("categorySearch", e.target.value)}
                      onFocus={() => setShowCategoryDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 200)}
                      autoComplete="off"
                    />
                    {showCategoryDropdown && expenseCategories.length > 0 ? (
                      <div
                        className="border rounded-3 mt-1 p-2 bg-white position-absolute w-100 shadow-sm"
                        style={{ maxHeight: "200px", overflowY: "auto", zIndex: 10 }}
                      >
                        {categoryDropdownOptions.length === 0 ? (
                          <div className="text-muted small">No match.</div>
                        ) : (
                          categoryDropdownOptions.map((c) => (
                            <Button
                              key={c.id}
                              variant="outline-secondary"
                              size="sm"
                              className="w-100 text-start mb-1"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelectCategory(c);
                              }}
                            >
                              {c.name}
                              {c.description ? ` · ${c.description}` : ""}
                            </Button>
                          ))
                        )}
                      </div>
                    ) : null}
                  </div>
                  {expenseCategories.length === 0 ? (
                    <p className="text-muted small mt-1 mb-0">
                      No expense types yet. Add them from <Link to={Routes.ExpenseCategories.path}>Expenses</Link> in the side menu.
                    </p>
                  ) : null}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Amount (Ksh)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={expenseForm.amount}
                    onChange={(e) => handleExpenseFormChange("amount", e.target.value)}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={expenseForm.expenseDate}
                    onChange={(e) => handleExpenseFormChange("expenseDate", e.target.value)}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Note (optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={expenseForm.note}
                    onChange={(e) => handleExpenseFormChange("note", e.target.value)}
                    placeholder="Optional note"
                  />
                </Form.Group>
                <Button type="submit" variant="primary" disabled={!canSaveExpense}>
                  Record expense
                </Button>
              </Form>
            </Card.Body>
          </Card>

          <Card border="light" className="shadow-sm mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Expenses</h5>
              <span className="text-muted">{formatAmount(totalExpensesAmount)}</span>
            </Card.Header>
            <Card.Body className="pt-0">
              {groupExpenses.length === 0 ? (
                <p className="text-muted mb-0">No expenses recorded yet.</p>
              ) : (
                <Table responsive className="table-centered mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th className="border-0">Type</th>
                      <th className="border-0 text-end">Amount</th>
                      <th className="border-0">Date</th>
                      <th className="border-0">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupExpenses.map((ex) => (
                      <tr key={ex.id}>
                        <td className="fw-bold">{ex.category?.name || ex.categoryName || "–"}</td>
                        <td className="text-end">{formatAmount(ex.amount)}</td>
                        <td>{formatTableDate(ex.expenseDate)}</td>
                        <td className="text-muted small">{ex.note || "–"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};
